/**
 * Runtime validation of an operator-submitted design-token file.
 *
 * The token file used to live in our own bucket, reviewed by us and staged
 * through `npm run operator:publish` before it ever shipped. It is now hosted
 * by the operator and handed to the app as a `?themeUrl=` query param, so
 * nothing has vetted it by the time it arrives - this module does at load time
 * what the publish pipeline used to do at review time.
 *
 * Rule-for-rule identical to design-system/scripts/validate-operator-theme.mjs
 * (and to pmu-mobile's copies of both), with ONE deliberate difference: the
 * WCAG contrast checks produce `warnings`, not `errors`. At publish time a
 * 4.3:1 pair should stop an operator from shipping; at runtime it must not make
 * a live partner's entire theme disappear over a shade.
 *
 * Why the rules are written twice rather than shared: the .mjs is a node CLI
 * and this is the Angular app - importing either from the other isn't worth
 * the build complexity. design-system/fixtures/operator-theme/ is the corpus
 * that holds the two in agreement; `npm run operator:fixtures` checks the CLI
 * side and operator-theme-schema.spec.ts checks this side, over the same files.
 * Change a rule here and you must change it in three other places.
 */

export interface ThemeValidationResult {
  valid: boolean;
  /** Reasons the token must not be applied at all. */
  errors: string[];
  /** Applied anyway, but worth surfacing (today: WCAG contrast). */
  warnings: string[];
}

const HEX_COLOR = /^#[0-9A-Fa-f]{6}$/;
const OPERATOR_ID = /^[a-z0-9][a-z0-9-]{0,63}$/;

const TOP_LEVEL_ALLOWED = ['schemaVersion', 'operator', 'color', 'typography', 'spacing', 'radius'];

const BRAND_REQUIRED = ['primary', 'primaryText', 'secondary', 'secondaryText'];
const BRAND_OPTIONAL = ['primaryLight'];
const CTA_REQUIRED = ['success', 'successText'];

/**
 * The full component vocabulary shared with hippique-online - NOT the subset
 * this app applies (that's TOKEN_PROPERTY_MAP in operator-theme.service.ts).
 * Unknown fields are a hard error, so a key missing here rejects an operator's
 * whole theme on this platform while the other one accepts the same file.
 */
const COMPONENT_SCHEMA: { [componentKey: string]: string[] } = {
  reunionTab: ['activeBg', 'activeText', 'activeBorder'],
  raceDateNav: ['bg', 'text', 'border'],
  betType: ['ring'],
  partantFormula: ['idleBg'],
  horseNumberBadge: ['bg', 'text'],
  betslipSpotBadge: ['bg', 'text'],
  rapportPositionBadge: ['bg', 'text'],
  rapportTiedBadge: ['bg', 'text'],
  raceStatusEnded: ['bg', 'text'],
  rowStriping: ['oddBg', 'evenBg'],
  rowNonPartant: ['bg']
};

const CONTRAST_PAIRS = [
  { fg: ['color', 'brand', 'primaryText'], bg: ['color', 'brand', 'primary'], minRatio: 4.5 },
  { fg: ['color', 'brand', 'secondaryText'], bg: ['color', 'brand', 'secondary'], minRatio: 4.5 },
  { fg: ['color', 'cta', 'successText'], bg: ['color', 'cta', 'success'], minRatio: 4.5 }
];

function fail(errors: string[], path: string, message: string): void {
  errors.push(`${path}: ${message}`);
}

function isPlainObject(value: any): boolean {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function checkObjectShape(
  obj: any,
  path: string,
  required: string[],
  optional: string[],
  errors: string[]
): void {

  if (!isPlainObject(obj)) {
    fail(errors, path, 'must be an object');

    return;
  }

  const allowed = required.concat(optional);

  Object.keys(obj).forEach(key => {
    if (allowed.indexOf(key) === -1) {
      fail(errors, `${path}.${key}`, 'unrecognized field - not part of the operator theme contract');
    }
  });

  required.forEach(key => {
    if (!(key in obj)) {
      fail(errors, `${path}.${key}`, 'required field is missing');
    }
  });
}

function checkHexColor(
  obj: any,
  path: string,
  key: string,
  errors: string[],
  required: boolean
): void {

  if (!(key in obj)) {
    if (required) {
      fail(errors, `${path}.${key}`, 'required field is missing');
    }

    return;
  }

  const value = obj[key];

  if (typeof value !== 'string' || !HEX_COLOR.test(value)) {
    fail(
      errors,
      `${path}.${key}`,
      `must be a 6-digit hex color (e.g. "#163CAD"), got ${JSON.stringify(value)}`
    );
  }
}

function checkNumberRange(
  obj: any,
  path: string,
  key: string,
  min: number,
  max: number,
  errors: string[]
): void {

  if (!(key in obj)) {
    return;
  }

  const value = obj[key];

  if (typeof value !== 'number' || isNaN(value)) {
    fail(errors, `${path}.${key}`, `must be a number, got ${JSON.stringify(value)}`);

    return;
  }

  if (value < min || value > max) {
    fail(errors, `${path}.${key}`, `must be between ${min} and ${max}, got ${value}`);
  }
}

// Relative luminance / contrast ratio per WCAG 2.x.
function relativeLuminance(hex: string): number {

  // Per-channel parse rather than the .mjs's bit-shifting; same values, the
  // input is always 6-digit hex by the time this runs.
  const rgb = [
    parseInt(hex.slice(1, 3), 16),
    parseInt(hex.slice(3, 5), 16),
    parseInt(hex.slice(5, 7), 16)
  ].map(channel => {
    const v = channel / 255;

    return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  });

  return 0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2];
}

function contrastRatio(hexA: string, hexB: string): number {

  const l1 = relativeLuminance(hexA);
  const l2 = relativeLuminance(hexB);

  return (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
}

function getPath(obj: any, segments: string[]): any {
  return segments.reduce(
    (acc, key) => (acc && typeof acc === 'object' ? acc[key] : undefined),
    obj
  );
}

export function validateOperatorTheme(theme: any): ThemeValidationResult {

  const errors: string[] = [];
  const warnings: string[] = [];

  if (!isPlainObject(theme)) {
    return { valid: false, errors: ['root: must be a JSON object'], warnings };
  }

  Object.keys(theme).forEach(key => {
    if (TOP_LEVEL_ALLOWED.indexOf(key) === -1) {
      fail(errors, key, 'unrecognized top-level field - not part of the operator theme contract');
    }
  });

  if (theme.schemaVersion !== '2.0') {
    fail(errors, 'schemaVersion', `must be exactly "2.0", got ${JSON.stringify(theme.schemaVersion)}`);
  }

  if (!theme.operator) {
    fail(errors, 'operator', 'required field is missing');
  } else {
    checkObjectShape(theme.operator, 'operator', ['id', 'displayName'], [], errors);

    if (typeof theme.operator.id === 'string' && !OPERATOR_ID.test(theme.operator.id)) {
      fail(errors, 'operator.id', 'must be lowercase, hyphenated (a-z0-9-), starting with a letter/digit');
    }

    if (typeof theme.operator.displayName === 'string' && theme.operator.displayName.length === 0) {
      fail(errors, 'operator.displayName', 'must not be empty');
    }
  }

  if (!theme.color) {
    fail(errors, 'color', 'required field is missing');
  } else {
    checkObjectShape(theme.color, 'color', ['brand', 'cta'], ['component'], errors);

    if (theme.color.brand) {
      checkObjectShape(theme.color.brand, 'color.brand', BRAND_REQUIRED, BRAND_OPTIONAL, errors);

      BRAND_REQUIRED.concat(BRAND_OPTIONAL).forEach(key => {
        checkHexColor(theme.color.brand, 'color.brand', key, errors, BRAND_REQUIRED.indexOf(key) !== -1);
      });
    }

    if (theme.color.cta) {
      checkObjectShape(theme.color.cta, 'color.cta', CTA_REQUIRED, [], errors);

      CTA_REQUIRED.forEach(key => {
        checkHexColor(theme.color.cta, 'color.cta', key, errors, true);
      });
    }

    if (theme.color.component) {
      checkObjectShape(
        theme.color.component,
        'color.component',
        [],
        Object.keys(COMPONENT_SCHEMA),
        errors
      );

      Object.keys(COMPONENT_SCHEMA).forEach(componentKey => {
        const componentValue = theme.color.component[componentKey];

        if (!componentValue) {
          return;
        }

        const path = `color.component.${componentKey}`;
        const fieldKeys = COMPONENT_SCHEMA[componentKey];

        checkObjectShape(componentValue, path, [], fieldKeys, errors);

        fieldKeys.forEach(key => {
          checkHexColor(componentValue, path, key, errors, false);
        });
      });
    }
  }

  if (theme.typography) {
    checkObjectShape(theme.typography, 'typography', [], ['scale', 'bodyLineHeight'], errors);
    checkNumberRange(theme.typography, 'typography', 'scale', 0.8, 1.2, errors);
    checkNumberRange(theme.typography, 'typography', 'bodyLineHeight', 1.2, 2.0, errors);
  }

  if (theme.spacing) {
    checkObjectShape(theme.spacing, 'spacing', [], ['density'], errors);
    checkNumberRange(theme.spacing, 'spacing', 'density', 0.8, 1.2, errors);
  }

  if (theme.radius) {
    checkObjectShape(theme.radius, 'radius', [], ['scale'], errors);
    checkNumberRange(theme.radius, 'radius', 'scale', 0, 2, errors);
  }

  // Accessibility - only meaningful once the format checks above have passed for
  // the pairs involved. Warnings, not errors: see the file header.
  if (errors.length === 0) {
    CONTRAST_PAIRS.forEach(pair => {

      const fg = getPath(theme, pair.fg);
      const bg = getPath(theme, pair.bg);

      if (typeof fg !== 'string' || typeof bg !== 'string') {
        return;
      }

      const ratio = contrastRatio(fg, bg);

      if (ratio < pair.minRatio) {
        warnings.push(
          `${pair.fg.join('.')}: contrast against ${pair.bg.join('.')} is ` +
          `${ratio.toFixed(2)}:1, below the recommended ${pair.minRatio}:1`
        );
      }
    });
  }

  return { valid: errors.length === 0, errors, warnings };
}
